import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Textarea } from 'src/@/components/ui/textarea';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function IdentificacaoSection({ aiAgent, changeModuleConfig, changePersona }) {
    const { t } = useTranslation('settings');
    const identificacao = aiAgent?.config?.module?.ecommerce?.identificacao || {};
    const telefones = Array.isArray(identificacao.telefonesSetor) ? identificacao.telefonesSetor : [];
    const persona = aiAgent?.config?.promptGuide?.persona || '';

    // Setores de varejo. Os `value` sao enum interno persistido em
    // telefonesSetor[].setor — nao traduzir.
    const setores = useMemo(
        () => [
            { value: 'vendas', label: t('aiAgents.ecommerce.identificacao.setorVendas', 'Vendas') },
            { value: 'sac', label: t('aiAgents.ecommerce.identificacao.setorSac', 'SAC') },
            { value: 'trocas_devolucoes', label: t('aiAgents.ecommerce.identificacao.setorTrocas', 'Trocas e devoluções') },
            { value: 'financeiro', label: t('aiAgents.ecommerce.identificacao.setorFinanceiro', 'Financeiro') },
            { value: 'outros', label: t('aiAgents.ecommerce.identificacao.setorOutros', 'Outros') },
        ],
        [t]
    );

    const updateIdentificacao = (patch) => {
        changeModuleConfig({
            identificacao: {
                ...identificacao,
                ...patch,
            },
        });
    };

    const addTelefone = () => {
        updateIdentificacao({ telefonesSetor: [...telefones, { setor: 'vendas', numero: '' }] });
    };

    const removeTelefone = (index) => {
        updateIdentificacao({ telefonesSetor: telefones.filter((_, i) => i !== index) });
    };

    const updateTelefone = (index, field, value) => {
        updateIdentificacao({
            telefonesSetor: telefones.map((tel, i) => (i === index ? { ...tel, [field]: value } : tel)),
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:storefront-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.ecommerce.identificacao.title', 'Identificação')}</CardTitle>
                        <CardDescription>{t('aiAgents.ecommerce.identificacao.subtitle', 'Quem é a assistente, qual é a loja e o que ela pode informar ao cliente.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.personaLabel', 'Nome da assistente')}</Label>
                        <Input
                            value={persona}
                            onChange={(e) => changePersona(e.target.value)}
                            placeholder={t('aiAgents.ecommerce.identificacao.personaPlaceholder', 'Lais')}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.ecommerce.identificacao.personaHint', 'Como ela se apresenta ao cliente no primeiro contato.')}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.nomeLabel', 'Nome da loja')}</Label>
                        <Input
                            value={identificacao.nome || ''}
                            onChange={(e) => updateIdentificacao({ nome: e.target.value })}
                            className={INPUT_CLS}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.siteLabel', 'Site da loja')}</Label>
                        <Input
                            type="url"
                            value={identificacao.site || ''}
                            onChange={(e) => updateIdentificacao({ site: e.target.value })}
                            placeholder="https://minhaloja.com.br"
                            className={INPUT_CLS}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.horariosLabel', 'Horários de atendimento')}</Label>
                        <Input
                            value={identificacao.horariosFuncionamento || ''}
                            onChange={(e) => updateIdentificacao({ horariosFuncionamento: e.target.value })}
                            placeholder={t('aiAgents.ecommerce.identificacao.horariosPlaceholder', 'Seg a sex, 9h às 18h')}
                            className={INPUT_CLS}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.enderecoLabel', 'Endereço / lojas físicas')}</Label>
                    <Textarea
                        value={identificacao.endereco || ''}
                        onChange={(e) => updateIdentificacao({ endereco: e.target.value })}
                        className={INPUT_CLS}
                        rows={2}
                    />
                    <p className={HINT_CLS}>{t('aiAgents.ecommerce.identificacao.enderecoHint', 'Usado quando o cliente pergunta onde retirar ou onde fica a loja.')}</p>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.identificacao.regiaoLabel', 'Região de atendimento e entrega')}</Label>
                    <Textarea
                        value={identificacao.regiaoAtendimento || ''}
                        onChange={(e) => updateIdentificacao({ regiaoAtendimento: e.target.value })}
                        className={INPUT_CLS}
                        rows={2}
                    />
                    <p className={HINT_CLS}>{t('aiAgents.ecommerce.identificacao.regiaoHint', 'Onde a loja entrega. A assistente usa isso para não prometer entrega fora da área.')}</p>
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">{t('aiAgents.ecommerce.identificacao.telefonesTitle', 'Telefones por setor')}</h4>
                            <p className="text-xs text-gray-500">{t('aiAgents.ecommerce.identificacao.telefonesSubtitle', 'Números que a assistente pode informar ao cliente.')}</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addTelefone}
                            className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                        >
                            <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                            {t('buttons.add', 'Adicionar', { ns: 'common' })}
                        </Button>
                    </div>

                    {telefones.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Iconify icon="mdi:phone-outline" width={24} className="text-gray-300" />
                            <p className="text-xs text-gray-500 mt-2">{t('aiAgents.ecommerce.identificacao.telefonesEmpty', 'Nenhum setor cadastrado')}</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {telefones.map((tel, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <Select
                                        value={tel.setor || 'vendas'}
                                        onValueChange={(value) => updateTelefone(index, 'setor', value)}
                                    >
                                        <SelectTrigger className={`${INPUT_CLS} w-44`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {setores.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        value={tel.numero || ''}
                                        onChange={(e) => updateTelefone(index, 'numero', e.target.value)}
                                        placeholder={t('aiAgents.ecommerce.identificacao.telefoneNumeroPlaceholder', '(11) 99999-9999')}
                                        className={`${INPUT_CLS} flex-1`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeTelefone(index)}
                                        className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    >
                                        <Iconify icon="eva:trash-2-outline" width={16} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
